/**
 * Enhanced lazy loading utility with error recovery and performance monitoring
 * Provides robust code splitting with automatic retry and fallback handling
 */
import React, { Suspense } from 'react';

const CHUNK_RELOAD_PREFIX = 'acuon:chunk-reload:';

export function isChunkLoadError(error) {
  const message = String(error?.message || error || '');
  const name = String(error?.name || '');
  return (
    name === 'ChunkLoadError' ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

export function reloadOnceForChunkError(error) {
  if (!isChunkLoadError(error) || typeof window === 'undefined') return false;

  const path = `${window.location.pathname}${window.location.search}`;
  const storageKey = `${CHUNK_RELOAD_PREFIX}${path}`;
  const alreadyReloaded = window.sessionStorage?.getItem(storageKey);
  if (alreadyReloaded) return false;

  try {
    window.sessionStorage?.setItem(storageKey, String(Date.now()));
  } catch (_) {
    // Continue with the reload even if storage is unavailable.
  }

  window.setTimeout(() => {
    window.location.reload();
  }, 100);
  return true;
}

function clearChunkReloadFlag() {
  if (typeof window === 'undefined') return;
  const path = `${window.location.pathname}${window.location.search}`;
  try {
    window.sessionStorage?.removeItem(`${CHUNK_RELOAD_PREFIX}${path}`);
  } catch (_) {}
}

/**
 * Create a lazy-loaded component with automatic retry on failure
 * Handles network errors, parsing errors, and chunk loading failures
 *
 * @param {Function} importFunc - Dynamic import function: () => import('./path/to/Component')
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Number of retry attempts (default: 3)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
 * @param {boolean} options.monitorPerformance - Log performance metrics (default: true)
 * @returns {React.Component} Lazy component with error handling
 */
export function lazyWithRetry(importFunc, options = {}) {
  const { maxRetries = 3, retryDelay = 1000, monitorPerformance = true } = options;

  let retryCount = 0;
  let importCache = null;

  const loadWithRetry = () => {
    const startTime = performance.now();

    return importFunc()
      .then((module) => {
        clearChunkReloadFlag();
        if (monitorPerformance) {
          const loadTime = performance.now() - startTime;
          logChunkLoadPerformance(loadTime, true);
        }
        importCache = module;
        return module;
      })
      .catch((error) => {
        retryCount++;

        if (monitorPerformance) {
          console.warn(
            `Chunk load failed (attempt ${retryCount}/${maxRetries}): ${error.message}`
          );
        }

        if (retryCount < maxRetries) {
          // Exponential backoff: 1000ms, 2000ms, 4000ms...
          const delay = retryDelay * Math.pow(2, retryCount - 1);
          console.log(`Retrying chunk load in ${delay}ms...`);

          return new Promise((resolve) => {
            setTimeout(() => resolve(loadWithRetry()), delay);
          });
        }

        // Final failure after all retries
        console.error(
          `Failed to load chunk after ${maxRetries} attempts:`,
          error
        );
        if (reloadOnceForChunkError(error)) {
          return new Promise(() => {});
        }
        throw error;
      });
  };

  // Return lazy component
  return React.lazy(loadWithRetry);
}

/**
 * Log chunk loading performance metrics
 * Can be integrated with analytics services
 *
 * @param {number} loadTime - Time taken to load chunk in milliseconds
 * @param {boolean} success - Whether the load was successful
 */
function logChunkLoadPerformance(loadTime, success) {
  const metric = {
    type: 'chunk_load',
    duration: loadTime,
    success,
    timestamp: new Date().toISOString(),
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.debug(
      `Chunk loaded in ${loadTime.toFixed(2)}ms`,
      metric
    );
  }

  // Could send to analytics here
  // analytics.trackEvent('chunk_load', metric);
}

/**
 * Loading fallback component with progress indicator
 * Shows while chunks are loading
 */
export function ChunkLoadingFallback() {
  return (
    <div className="chunk-loading-container">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

/**
 * Wrapper component to add Suspense and error boundary to lazy components
 *
 * @param {React.Component} LazyComponent - A React.lazy() component
 * @param {React.Component} Fallback - Fallback component (default: ChunkLoadingFallback)
 * @returns {React.Component} Component with Suspense wrapper
 */
export function withChunkSuspense(LazyComponent, Fallback = ChunkLoadingFallback) {
  return function SuspenseWrapper(props) {
    return (
      <Suspense fallback={<Fallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

/**
 * Advanced lazy loading with preloading capability
 * Allows preloading chunks before route navigation
 *
 * @param {Function} importFunc - Dynamic import function
 * @param {Object} options - Configuration options
 * @returns {Object} Component and preload function
 */
export function lazyWithPreload(importFunc, options = {}) {
  const LazyComponent = lazyWithRetry(importFunc, options);

  return {
    Component: LazyComponent,
    preload: () => {
      try {
        importFunc();
      } catch (error) {
        console.warn('Preload failed:', error);
      }
    },
  };
}

/**
 * Utility to create all lazy page imports at once
 * Useful for code clarity and centralized management
 *
 * @param {Object} pageModules - Object mapping page names to import functions
 * @returns {Object} Object mapping page names to lazy components
 *
 * @example
 * const pages = createLazyPages({
 *   Dashboard: () => import('./pages/Dashboard'),
 *   AnalyticsView: () => import('./pages/AnalyticsView'),
 * });
 * // pages.Dashboard is now a lazy component with retry support
 */
export function createLazyPages(pageModules) {
  const lazyPages = {};

  Object.entries(pageModules).forEach(([name, importFunc]) => {
    lazyPages[name] = lazyWithRetry(importFunc, {
      maxRetries: 3,
      retryDelay: 1000,
      monitorPerformance: true,
    });
  });

  return lazyPages;
}

/**
 * Performance monitoring for chunk loading
 * Tracks metrics over time
 */
class ChunkLoadingMonitor {
  constructor() {
    this.metrics = [];
    this.failures = [];
  }

  recordSuccess(chunkName, duration) {
    this.metrics.push({
      chunk: chunkName,
      duration,
      success: true,
      timestamp: Date.now(),
    });
  }

  recordFailure(chunkName, error) {
    this.failures.push({
      chunk: chunkName,
      error: error.message,
      timestamp: Date.now(),
    });
  }

  getStats() {
    if (this.metrics.length === 0) {
      return { totalLoads: 0, avgDuration: 0, failureRate: 0 };
    }

    const totalLoads = this.metrics.length + this.failures.length;
    const avgDuration =
      this.metrics.reduce((sum, m) => sum + m.duration, 0) /
      this.metrics.length;
    const failureRate = (this.failures.length / totalLoads) * 100;

    return {
      totalLoads,
      successfulLoads: this.metrics.length,
      failedLoads: this.failures.length,
      avgDuration: avgDuration.toFixed(2),
      failureRate: failureRate.toFixed(2),
    };
  }

  log() {
    console.table(this.getStats());
  }
}

// Global monitor instance
export const chunkLoadingMonitor = new ChunkLoadingMonitor();

/**
 * CSS for loading fallback
 * Should be included in main stylesheet
 */
export const chunkLoadingStyles = `
  .chunk-loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    gap: 16px;
    color: var(--color-text-secondary);
  }

  .chunk-loading-container .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-border-primary);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .chunk-loading-container p {
    font-size: 14px;
    margin: 0;
  }
`;
